import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { mediaUrl, trilhaJson } from './api';

export default function TrilhaPastasPage() {
  const { setorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [setor, setSetor] = useState(null);
  const [pastas, setPastas] = useState([]);
  const [open, setOpen] = useState(false);
  const [nome, setNome] = useState('');
  const [observacao, setObservacao] = useState('');
  const [capa, setCapa] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await trilhaJson(`/api/trilha-aprendizado/setores/${setorId}/pastas`);
      setSetor(data.setor || null);
      setPastas(data.pastas || []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar pastas.');
    } finally {
      setLoading(false);
    }
  }, [setorId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [load]);

  const handleCreate = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('nome', nome.trim());
      fd.append('observacao', observacao.trim());
      if (capa) fd.append('capa', capa);
      await trilhaJson(`/api/trilha-aprendizado/setores/${setorId}/pastas`, {
        method: 'POST',
        body: fd,
      });
      setOpen(false);
      setNome('');
      setObservacao('');
      setCapa(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pasta) => {
    if (!window.confirm(`Excluir a pasta "${pasta.nome}"?`)) return;
    try {
      await trilhaJson(`/api/trilha-aprendizado/pastas/${pasta.id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const canWrite = !!setor?.canWrite;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/trilha-aprendizado')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Setores
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#00695c' }}>
            {setor?.nome || 'Setor'}
          </Typography>
          {setor?.descricao && (
            <Typography variant="body2" color="text.secondary">
              {setor.descricao}
            </Typography>
          )}
        </Box>
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ bgcolor: '#00897b' }}>
            Nova Pasta
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#00897b' }} />
        </Box>
      ) : pastas.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FolderOpenIcon sx={{ fontSize: 56, color: '#80cbc4', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma pasta neste setor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canWrite ? 'Adicione a primeira pasta de treinamento.' : 'Ainda não há pastas publicadas.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {pastas.map((pasta) => (
            <Grid item xs={12} sm={6} md={4} key={pasta.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(0,137,123,0.12)',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(0,137,123,0.15)' },
                }}
              >
                <CardActionArea onClick={() => navigate(`/trilha-aprendizado/pastas/${pasta.id}`)}>
                  {pasta.capaUrl ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={mediaUrl(pasta.capaUrl)}
                      alt={pasta.nome}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FolderOpenIcon sx={{ fontSize: 48, color: '#00897b' }} />
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} noWrap>
                      {pasta.nome}
                    </Typography>
                    {pasta.observacao && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                        {pasta.observacao}
                      </Typography>
                    )}
                    <Chip size="small" label={`${pasta.conteudosCount || 0} conteúdo(s)`} sx={{ mt: 1.5 }} />
                  </CardContent>
                </CardActionArea>
                {canWrite && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1 }}>
                    <IconButton size="small" color="error" onClick={() => handleDelete(pasta)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nova pasta</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth autoFocus />
          <TextField
            label="Observação"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Button variant="outlined" component="label">
            {capa ? capa.name : 'Imagem de capa'}
            <input hidden type="file" accept="image/*" onChange={(e) => setCapa(e.target.files?.[0] || null)} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !nome.trim()}
            sx={{ bgcolor: '#00897b' }}
          >
            {saving ? 'Salvando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
